/**
 * The shapes global search speaks, mirrored from portal-be's `api.search.dto` package. Every search
 * surface — the bar, the dropdown, the peek card, the results page and the narrow sheet — reads this
 * one file, so none of them can drift from the contract or from each other.
 */

/**
 * The record families search covers, in the backend's declaration order. That order is also the
 * precedence used when a query is an exact identifier in more than one family, so never reorder it
 * to suit a screen and never write a second ordered list beside it.
 *
 * Pages and actions are not a family: they have no row in any tenant schema and their routes are the
 * client's own, so `useSearchableRoutes` supplies them and the dropdown merges them in on this side.
 */
export const GLOBAL_SEARCH_ENTITY_TYPES = [
    'MATTER',
    'INVOICE',
    'PARTY',
    'PREPAYMENT',
    'TIMECARD',
    'EFFORT',
    'EXPENSE',
    'CORRESPONDENCE',
    // Bank transfers, as the rest of the frontend calls them. The backend's noun is the table's.
    'BANK_TRANSACTION',
    'CONTRACT',
    'PRICELIST',
    'PROJECT',
    'EXPERIENCE_RECORD',
    'DEBRIEFING',
    'PARTY_GROUP',
    'EMPLOYEE',
    'TEAM',
    'OFFICE',
    'PRACTICE_AREA',
    'JOB_POSITION',
    'JOB_ROLE',
    'DELEGATION',
] as const;

export type GlobalSearchEntityType = (typeof GLOBAL_SEARCH_ENTITY_TYPES)[number];

/**
 * Whether this build knows a record family.
 *
 * The backend deploys ahead of this one, so a family it has gained and this build has not can
 * arrive in a response. Every family is drawn from three per-family maps — its glyph, its plural
 * name and its detail route — and none of them has an entry for a family that is not in the list
 * above, so a row of an unknown family cannot be drawn at all. It is dropped where the response is
 * read rather than guarded at each of those three places.
 */
export function isGlobalSearchEntityType(value: string): value is GlobalSearchEntityType {
    return (GLOBAL_SEARCH_ENTITY_TYPES as readonly string[]).includes(value);
}

/** Which surface is asking. It decides which parts of the response are filled. */
export type GlobalSearchMode = 'DROPDOWN' | 'RESULTS';

/** How a status label is to be coloured. The tone describes the state; the colour is ours to pick. */
export type GlobalSearchStatusTone = 'SUCCESS' | 'WARNING' | 'ERROR' | 'INFO' | 'NEUTRAL';

/** The ranges the "updated" facet offers. They partition the timeline — a record falls in one. */
export type GlobalSearchUpdatedBucket = 'TODAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'THIS_YEAR' | 'OLDER';

/** What the second line of a row holds: a quote of the matched body text, or the record's facts. */
export type GlobalSearchSecondaryLineKind = 'QUOTE' | 'FACTS';

/** The cues a row can carry, in the order they outrank each other. */
export type GlobalSearchRowCueType = 'RESPONSIBLE' | 'INVOLVED' | 'PARTIAL_TERM_MATCH';

/** The dimensions the results page narrows by. */
export type GlobalSearchFacetDimension = 'ENTITY_TYPE' | 'OFFICE' | 'RESPONSIBLE' | 'PRACTICE_AREA' | 'UPDATED';

/**
 * Opens a highlighted run inside a row's `displayName` or `secondaryLine`. Deliberately not HTML:
 * split on the sentinels and build the element (see `HighlightedText`), so no search result ever
 * becomes markup a browser parses.
 */
export const HIGHLIGHT_START = '[[hl]]';

/** Closes a run opened by {@link HIGHLIGHT_START}. */
export const HIGHLIGHT_END = '[[/hl]]';

/** Shortest word a query must hold for the backend to answer it; without one it is rejected, not answered empty. */
export const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 3;

const SEARCHABLE_WORD = new RegExp(`\\S{${GLOBAL_SEARCH_MIN_QUERY_LENGTH}}`, 'u');

/** The backend's own rule: `ab cd` is rejected however long it is, because no word of it is long enough. */
export function isSearchableQuery(query: string): boolean {
    return SEARCHABLE_WORD.test(query);
}

/**
 * Whether a failed request is a search the backend gave up on: the query matched too much to rank in time,
 * and the user can narrow it. Any other failure is an outage as far as the search surfaces are concerned.
 */
export function isTimedOutSearch(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !('data' in error)) {
        return false;
    }
    return (error.data as { code?: unknown } | null | undefined)?.code === 'SEARCH_TIMED_OUT';
}

/**
 * Where the search stops being drawn wide: the dropdown gives way to the sheet, and the facet rail
 * stops standing beside the results and folds above them.
 *
 * It is `--breakpoint-sm`, which is 768px here — the width at which the breadcrumb trail already
 * hides and the search box already has the header row to itself. The design draws the narrow frame
 * at 430px, but that is a canvas width and not a second breakpoint. It lives here, with the other
 * constants both the chrome and the results page read, so the two surfaces cannot drift apart.
 */
export const GLOBAL_SEARCH_NARROW_QUERY = '(max-width: 767.98px)';

/** How many recently-opened records the backend reads as a ranking hint. The rest are ignored. */
export const GLOBAL_SEARCH_MAX_RECENT_IDS = 20;

/** The single cue for a row's right-hand column: a relationship, or how many terms matched. */
export interface GlobalSearchRowCue {
    type: GlobalSearchRowCueType;
    /** Set only for PARTIAL_TERM_MATCH. */
    matchedTerms: number | null;
    /** Set only for PARTIAL_TERM_MATCH. */
    totalTerms: number | null;
}

/** One matched record: which family it belongs to, its id, what to draw, and why it matched. */
export interface GlobalSearchRow {
    entityType: GlobalSearchEntityType;
    /** Ids are per-family, so a row is identified by `entityType` and `id` together. */
    id: number;
    /** Plain text carrying the highlight sentinels. Never inject it as HTML. */
    displayName: string;
    /** A quote of the matched text or the record's facts, per `secondaryLineKind`. */
    secondaryLine: string | null;
    /** Null exactly when `secondaryLine` is null. */
    secondaryLineKind: GlobalSearchSecondaryLineKind | null;
    /** ISO-8601 instant. */
    updatedAt: string;
    cue: GlobalSearchRowCue | null;
    /** A draft the caller owns or was shared: false for a family that has none. */
    isDraft: boolean;
}

/** One entity family's group in the dropdown: its whole count, and the best few of its rows. */
export interface GlobalSearchSection {
    entityType: GlobalSearchEntityType;
    count: number;
    rows: GlobalSearchRow[];
}

/** One selectable facet value: what to send to apply it, what to show, and how many carry it. */
export interface GlobalSearchFacetValue {
    /** The id for OFFICE, RESPONSIBLE and PRACTICE_AREA; the enum name for ENTITY_TYPE and UPDATED. */
    value: string;
    label: string;
    count: number;
}

/** One dimension of the facet rail, with every value that has at least one visible match. */
export interface GlobalSearchFacetGroup {
    dimension: GlobalSearchFacetDimension;
    /** Untruncated: how many to show before a "show more" is this side's decision. */
    values: GlobalSearchFacetValue[];
}

/** A better spelling offered for a query that looks misspelled. */
export interface GlobalSearchDidYouMean {
    term: string;
    resultCount: number;
}

/**
 * The one response both surfaces read. The dropdown fills `bestBet` and `sections`, the results page
 * fills `rows`, `facets` and the paging fields; the shape is the same either way.
 */
export interface GlobalSearchResponse {
    /** The exact-identifier hit, and the row Enter opens. Null when nothing matched exactly. */
    bestBet: GlobalSearchRow | null;
    sections: GlobalSearchSection[];
    rows: GlobalSearchRow[];
    facets: GlobalSearchFacetGroup[];
    /** Counted within the match-set cap. Read it together with `capped`. */
    total: number;
    /** True when the match set filled the cap, which makes `total` a floor — render it as "total+". */
    capped: boolean;
    /** What to send as `cursor` for the next page. Null when exhausted, and always null for DROPDOWN. */
    nextCursor: number | null;
    didYouMean: GlobalSearchDidYouMean | null;
}

/** One of the peek card's figures, already formatted for display by the backend. */
export interface GlobalSearchPeekFact {
    label: string;
    value: string;
}

/**
 * What the hover peek shows for one record — the same shape for every family.
 *
 * Everything on it is already formatted for display: money carries its currency, hours their unit,
 * dates the portal's format and the status its own English wording. Nothing here is reformatted or
 * translated on this side — the two things the card decides for itself are the glyph and what the
 * tone looks like.
 */
export interface GlobalSearchPeekCard {
    entityType: GlobalSearchEntityType;
    /** The record's id within its family. Ids are per-family, so it is read with `entityType`. */
    id: number;
    /**
     * Which glyph to draw. It is the entity family for most families; one whose records come in
     * kinds says which kind, so a contact arrives as `PARTY_PERSON` or `PARTY_COMPANY`.
     */
    iconKey: string;
    title: string;
    /** `Type · reference · office`. */
    subtitle: string | null;
    /**
     * The status and the tone to colour it by. The label is English, derived from the stored code
     * rather than from the tenant's own wording for it, so it is drawn as it arrives and never
     * translated here.
     */
    status: { label: string; tone: GlobalSearchStatusTone } | null;
    /** The record's one-line description or the matched quote. */
    description: string | null;
    /** At most four — the grid is two columns of two. */
    facts: GlobalSearchPeekFact[];
    /**
     * The action kinds that currently apply to this record, never a URL: the client already owns
     * the map from a family to its route. `COPY_LINK` and `AI_SUMMARY` come on every card (the
     * latter only where the assistant is switched on); `LOG_TIME`, `SEND_REMINDER` and
     * `OPEN_PROFILE` are a family's own and arrive only while they apply. `LOG_TIME` and
     * `SEND_REMINDER` are checked against the family again before they are drawn, because each has
     * one route this side; `OPEN_PROFILE` is drawn for whatever family it arrives with.
     *
     * Deliberately not a union: a kind a newer backend offers is ignored by the card rather than
     * failing to parse, which is also how a kind this client cannot carry out is dropped.
     */
    actions: string[];
}
